import { useState } from 'react'
import { Button } from 'design-system/ui/atoms/button'
import {
  Code2,
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  Key,
  RefreshCw,
} from 'lucide-react'

import { toast } from '~/design-system/atom/use-toast'
import { copyText } from '~/utils/dom'

interface WebhookSecretSectionProps {
  onRegenerate: () => void
  secret: string | undefined
  isLoading: boolean
}

export function WebhookSecretSection({
  onRegenerate,
  secret,
  isLoading,
}: WebhookSecretSectionProps) {
  const [isVisible, setIsVisible] = useState(false)

  const handleRegenerate = () => {
    onRegenerate()
  }

  const hasSecret = !!secret

  return (
    <div>
      <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white shadow-sm dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-950">
        {isLoading ? (
          <div className="m-auto flex min-h-[300px] w-[900px] flex-col items-start px-6 py-5">
            <div role="status" className="w-full animate-pulse">
              <div className="mb-4 h-2.5 w-48 rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div className="mb-2.5 h-2 max-w-[360px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div className="mb-2.5 h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div className="mb-2.5 h-2 max-w-[330px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div className="mb-2.5 h-2 max-w-[300px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-2 max-w-[360px] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        ) : (
          <div>
            <div className="px-6 py-5">
              <div className="flex items-center gap-3">
                <Key className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Webhook Secret
                </h3>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Use this secret to authenticate and verify the authenticity of
                incoming webhooks
              </p>
            </div>

            <div className="space-y-5 px-6 py-5">
              <div className="flex w-full space-x-2">
                {hasSecret && (
                  <div className="flex w-[500px] max-w-2xl items-center gap-2 rounded-lg border bg-gray-50 px-2 shadow-sm">
                    <div className="flex-1 truncate font-mono text-sm text-gray-800">
                      {isVisible
                        ? secret
                        : '•'.repeat(secret.length - 4) + secret.slice(-4)}
                    </div>
                    <button
                      onClick={() => setIsVisible(!isVisible)}
                      className="p-2 text-gray-500 transition-colors hover:text-gray-700 "
                      aria-label={isVisible ? 'Hide API key' : 'Show API key'}
                    >
                      {isVisible ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        if (secret) {
                          copyText(secret)
                          toast({
                            title: 'Copied',
                            description: 'Webhook Secret copied to clipboard',
                          })
                        }
                      }}
                    >
                      <CopyIcon className="h-4 w-4 text-gray-500 transition-colors hover:text-gray-700" />
                    </button>
                  </div>
                )}
                <Button onClick={handleRegenerate} variant="primary" size="36">
                  <RefreshCw className="mr-1 h-4 w-4" />
                  {hasSecret ? 'Regenerate' : 'Generate'}
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3">
                  <Code2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      This secret allows you to securely verify incoming
                      webhooks' authenticity. It creates a unique signature for
                      each request using the secret and payload.
                    </p>
                    <a
                      href="https://github.com/standard-webhooks/standard-webhooks/blob/main/README.md"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:text-indigo-500 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300"
                    >
                      View Webhooks Documentation
                    </a>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-2 dark:border-gray-800 dark:bg-gray-900">
                  <p className="mb-0 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pro tip: Keep this secret secure and never expose it in
                    client-side code.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
